import { useState, useEffect } from 'react'
import { useTeamsSDK } from 'use-teams-sdk'
import { useRouter } from 'next/router'

const detectInitialMobile = () => {
  if (process.env.NEXT_PUBLIC_FORCE_DESKTOP === 'true') return false
  if (typeof window === 'undefined') return false

  const userAgent =
    window.navigator.userAgent || window.navigator.vendor || (window as any).opera
  const mobileUserAgent =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      userAgent
    )
  const compactTaskModule = window.innerWidth <= 800 && window.innerHeight <= 900

  return mobileUserAgent || compactTaskModule
}

const detectTeamsContext = () => {
  if (typeof window === 'undefined') return false

  const url = window.location.href
  return (
    /teamstab|teamschat|messenger|taskModule/.test(url) ||
    Boolean(window.name && /embedded|iframe/.test(window.name)) ||
    typeof (window as any).microsoftTeams !== 'undefined' ||
    document.documentElement.classList.contains('teams-dark') ||
    document.documentElement.classList.contains('teams-light')
  )
}

/**
 * Detects if the current client is a mobile device
 * This first tries to check via the Teams SDK for Teams mobile clients
 * Then falls back to browser-based detection for standalone mobile browsers
 */
export function useTeamsMobile() {
  const [isMobile, setIsMobile] = useState<boolean>(
    () => detectInitialMobile() && detectTeamsContext()
  )
  const teamsSDKPromise = useTeamsSDK()
  const router = useRouter()

  useEffect(() => {
    // Check if force desktop mode is enabled via environment variable
    if (process.env.NEXT_PUBLIC_FORCE_DESKTOP === 'true') {
      console.log('Desktop mode forced via environment variable')
      setIsMobile(false)
      return
    }
    
    // Special case for debug/testing: Check for mobile URL parameter
    if (router.query.mobile === 'true' || router.query.mobile === '1') {
      console.log('Mobile mode forced via URL parameter')
      setIsMobile(true)
      return
    }
    
    // Browser-based detection first as fallback
    function detectMobileBrowser() {
      // Check if window is available (client-side)
      if (typeof window !== 'undefined') {
        const userAgent = window.navigator.userAgent || window.navigator.vendor || (window as any).opera

        // Regular expressions for mobile detection
        const regexMobile1 = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i
        const regexMobile2 = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i

        const isMobileBrowser = regexMobile1.test(userAgent) || 
                              regexMobile2.test(userAgent.substr(0, 4)) ||
                              (window.innerWidth <= 800 && window.innerHeight <= 900)
        
        return isMobileBrowser
      }
      return false
    }

    // First check if it's a mobile browser
    const isMobileBrowser = detectMobileBrowser()
    const isLikelyTeamsMobile = isMobileBrowser && detectTeamsContext()

    if (isLikelyTeamsMobile) {
      console.log('Mobile browser detected via UserAgent or screen size')
      setIsMobile(true)
    }

    // Then try Teams SDK detection for Teams mobile clients
    async function detectTeamsMobileClient() {
      try {
        const teamsSDK = await teamsSDKPromise
        if (!teamsSDK) {
          setIsMobile(isLikelyTeamsMobile)
          return
        }

        const context = await teamsSDK.app.getContext()
        // hostClientType can be: 'android', 'ios', 'desktop', 'web', 'rigel'
        const hostClientType = context.app?.host?.clientType?.toLowerCase()
        const isTeamsMobile = hostClientType === 'android' || hostClientType === 'ios'
        if (isTeamsMobile) {
          console.log('Teams mobile client detected via Teams SDK')
        }
        setIsMobile(isTeamsMobile)
      } catch (error) {
        console.error('Failed to detect Teams platform:', error)
        // Fallback to browser detection if Teams SDK fails
        setIsMobile(isLikelyTeamsMobile)
      }
    }

    detectTeamsMobileClient()
  }, [teamsSDKPromise, router.query])

  return isMobile
}
